import React, { useEffect, useState, useRef } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
interface DateRange {
  start: Date | null;
  end: Date | null;
}
interface DateRangePickerProps {
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
}
type Preset = {
  label: string;
  getValue: () => DateRange;
};
const formatInputDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
export function DateRangePicker({
  dateRange,
  onDateRangeChange
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const isNullRange = dateRange.start === null && dateRange.end === null;
  const [activePreset, setActivePreset] = useState<string>(isNullRange ? 'più recenti' : 'questo mese');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const presets: Preset[] = [{
    label: 'Più recenti',
    getValue: () => ({ start: null, end: null }),
  }, {
    label: 'Oggi',
    getValue: () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return {
        start: today,
        end: today
      };
    }
  }, {
    label: 'Ieri',
    getValue: () => {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0);
      return {
        start: yesterday,
        end: yesterday
      };
    }
  }, {
    label: 'Ultimi 7 giorni',
    getValue: () => {
      const end = new Date();
      end.setHours(0, 0, 0, 0);
      const start = new Date();
      start.setDate(start.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      return {
        start,
        end
      };
    }
  }, {
    label: 'Questo mese',
    getValue: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return {
        start,
        end
      };
    }
  }, {
    label: 'Mese scorso',
    getValue: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return {
        start,
        end
      };
    }
  }];
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  const formatDateRange = () => {
    if (dateRange.start === null && dateRange.end === null) return 'Più recenti';
    if (dateRange.start === null) return `Fino al ${formatDate(dateRange.end!)}`;
    if (dateRange.end === null) return `Dal ${formatDate(dateRange.start)}`;
    const start = formatDate(dateRange.start);
    const end = formatDate(dateRange.end);
    return start === end ? start : `${start} - ${end}`;
  };
  const handlePresetClick = (preset: Preset, label: string) => {
    const range = preset.getValue();
    onDateRangeChange(range);
    setActivePreset(label.toLowerCase());
    setIsOpen(false);
  };
  const handleCustomDateChange = (type: 'start' | 'end', value: string) => {
    const newDate = new Date(value);
    newDate.setHours(0, 0, 0, 0);
    if (type === 'start') {
      onDateRangeChange({
        ...dateRange,
        start: newDate
      });
    } else {
      onDateRangeChange({
        ...dateRange,
        end: newDate
      });
    }
    setActivePreset('personalizzato');
  };
  return <div className="relative" ref={dropdownRef}>
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-lg hover:border-slate-400 transition-colors">
        <Calendar className="w-5 h-5 text-slate-600" />
        <span className="text-sm font-medium text-slate-900">
          {formatDateRange()}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-600 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && <div className="absolute top-full left-0 mt-2 w-96 bg-white border border-slate-200 rounded-lg shadow-xl z-50">
          <div className="flex">
            {/* Presets */}
            <div className="w-40 border-r border-slate-200 p-2">
              {presets.map(preset => <button key={preset.label} onClick={() => handlePresetClick(preset, preset.label)} className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${activePreset === preset.label.toLowerCase() ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700 hover:bg-slate-50'}`}>
                  {preset.label}
                </button>)}
            </div>

            {/* Custom Date Inputs */}
            <div className="flex-1 p-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2">
                    Data inizio
                  </label>
                  <input type="date" value={dateRange.start ? formatInputDate(dateRange.start) : ''} onChange={e => handleCustomDateChange('start', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2">
                    Data fine
                  </label>
                  <input type="date" value={dateRange.end ? formatInputDate(dateRange.end) : ''} onChange={e => handleCustomDateChange('end', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>

                <button onClick={() => setIsOpen(false)} className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
                  Applica
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}